/**
 * Nominatim Geocoding Service
 *
 * Service for interacting with OpenStreetMap Nominatim API
 * with proper rate limiting and Polish region optimization.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  type NominatimGeoJSONResponse,
  type MapLibreGeocoderResponse,
  type MapLibreGeocoderFeature,
  type GeocodingRequest,
  type ReverseGeocodingRequest,
  type GeocodingResult,
  type GeocodingError,
  GeocodingErrorType,
  type AddressComponents,
  type NominatimGeoJSONProperties,
} from '../../types/geocoding/index.js';
import {
  getNominatimConfig,
  type NominatimConfig,
} from '../../config/nominatim.js';
import { nominatimRateLimiter } from './rateLimiter.js';

/** Default administrative zoom level for reverse geocoding (building detail). */
const DEFAULT_REVERSE_ZOOM = '18';

/**
 * Nominatim geocoding service class
 */
class NominatimService {
  private readonly config: NominatimConfig;
  private readonly httpClient: AxiosInstance;

  constructor(customConfig?: Partial<NominatimConfig>) {
    this.config = { ...getNominatimConfig(), ...customConfig };

    this.httpClient = axios.create({
      baseURL: this.config.baseUrl,
      timeout: this.config.timeoutMs,
      headers: {
        Accept: 'application/json',
        'Accept-Language': this.config.defaultParams['accept-language'],
      },
    });
  }

  /**
   * Forward geocoding: Convert address/place name to coordinates
   */
  async geocode(request: GeocodingRequest): Promise<GeocodingResult> {
    return nominatimRateLimiter.execute(async () => {
      try {
        const params = this.buildGeocodingParams(request);

        const response = await this.httpClient.get<NominatimGeoJSONResponse>(
          '/search',
          {
            params,
          }
        );

        const mapLibreResponse = this.convertToMapLibreFormat(response.data);

        if (mapLibreResponse.features.length === 0) {
          return {
            success: false,
            error: {
              type: GeocodingErrorType.NO_RESULTS,
              message: `No results found for query: "${request.query}"`,
            },
          };
        }

        return {
          success: true,
          data: mapLibreResponse,
        };
      } catch (error) {
        return {
          success: false,
          error: this.handleError(error),
        };
      }
    });
  }

  /**
   * Reverse geocoding: Convert coordinates to address
   */
  async reverseGeocode(
    request: ReverseGeocodingRequest
  ): Promise<GeocodingResult> {
    return nominatimRateLimiter.execute(async () => {
      try {
        const params = request.unbounded
          ? this.buildUnboundedReverseParams(request)
          : this.buildBoundedReverseParams(request);

        const response = await this.httpClient.get<NominatimGeoJSONResponse>(
          '/reverse',
          {
            params,
          }
        );

        const mapLibreResponse = this.convertToMapLibreFormat(response.data);

        if (mapLibreResponse.features.length === 0) {
          return {
            success: false,
            error: {
              type: GeocodingErrorType.NO_RESULTS,
              message: `No address found for coordinates: ${request.coordinates.lat}, ${request.coordinates.lng}`,
            },
          };
        }

        return {
          success: true,
          data: mapLibreResponse,
        };
      } catch (error) {
        return {
          success: false,
          error: this.handleError(error),
        };
      }
    });
  }

  /**
   * Build reverse-geocoding params using the Poland-optimized defaults
   * (countrycodes / viewbox / bounded inherited from config).
   */
  private buildBoundedReverseParams(
    request: ReverseGeocodingRequest
  ): Record<string, string> {
    const defaultParams = Object.entries(this.config.defaultParams).reduce(
      (acc, [key, value]) => {
        acc[key] = value.toString();
        return acc;
      },
      {} as Record<string, string>
    );

    return {
      ...defaultParams,
      lat: request.coordinates.lat.toString(),
      lon: request.coordinates.lng.toString(),
      zoom: request.zoom?.toString() || DEFAULT_REVERSE_ZOOM,
      'accept-language':
        request.language || this.config.defaultParams['accept-language'],
    };
  }

  /**
   * Build worldwide reverse-geocoding params from an explicit whitelist.
   * Deliberately omits countrycodes / viewbox / bounded so coordinates
   * outside Poland resolve correctly.
   */
  private buildUnboundedReverseParams(
    request: ReverseGeocodingRequest
  ): Record<string, string> {
    return {
      format: this.config.defaultParams.format,
      addressdetails: this.config.defaultParams.addressdetails.toString(),
      'accept-language':
        request.language || this.config.defaultParams['accept-language'],
      zoom: request.zoom?.toString() || DEFAULT_REVERSE_ZOOM,
      lat: request.coordinates.lat.toString(),
      lon: request.coordinates.lng.toString(),
    };
  }

  /**
   * Build geocoding request parameters
   */
  private buildGeocodingParams(
    request: GeocodingRequest
  ): Record<string, string> {
    // Convert config default params to strings
    const defaultParams = Object.entries(this.config.defaultParams).reduce(
      (acc, [key, value]) => {
        acc[key] = value.toString();
        return acc;
      },
      {} as Record<string, string>
    );

    const params: Record<string, string> = {
      ...defaultParams,
      q: request.query,
    };

    // Override limit if specified
    if (request.limit) {
      params.limit = request.limit.toString();
    }

    // Set language if specified
    if (request.language) {
      params['accept-language'] = request.language;
    }

    // Set country restrictions if specified
    if (request.countries && request.countries.length > 0) {
      params.countrycodes = request.countries.join(',');
    }

    // Set bounding box if specified
    if (request.bbox) {
      params.viewbox = [
        request.bbox.southwest.lng,
        request.bbox.southwest.lat,
        request.bbox.northeast.lng,
        request.bbox.northeast.lat,
      ].join(',');
      params.bounded = '1';
    }

    // Set proximity if specified
    if (request.proximity) {
      params.lat = request.proximity.lat.toString();
      params.lon = request.proximity.lng.toString();
    }

    // All params are already converted to strings above

    return params;
  }

  /**
   * Convert Nominatim GeoJSON response to MapLibre Geocoder format
   */
  private convertToMapLibreFormat(
    response: NominatimGeoJSONResponse
  ): MapLibreGeocoderResponse {
    const features: MapLibreGeocoderFeature[] = response.features.map(
      (feature) => {
        const coordinates = feature.geometry.coordinates;
        const properties = feature.properties;

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates,
          },
          place_name: properties.display_name,
          properties,
          text: this.extractShortText(properties),
          place_type: [this.categorizePlace(properties)],
          center: coordinates,
          bbox: feature.bbox,
          context: this.buildAddressContext(properties.address),
        };
      }
    );

    return { features };
  }

  /**
   * Extract short text representation from properties
   */
  private extractShortText(properties: NominatimGeoJSONProperties): string {
    const address = properties.address;
    const displayName = properties.display_name || '';

    if (!address) {
      return displayName.split(',')[0]?.trim() || '';
    }

    // Priority order for short text
    const textPriority = [
      address.house_number && address.road
        ? `${address.road} ${address.house_number}`
        : null,
      address.road,
      address.suburb,
      address.city,
      address.town,
      address.village,
      address.municipality,
    ].filter((text): text is string => Boolean(text));

    return textPriority[0] || displayName.split(',')[0]?.trim() || '';
  }

  /**
   * Categorize place type for MapLibre
   */
  private categorizePlace(properties: NominatimGeoJSONProperties): string {
    const type = String(properties.type);
    const osm_class = String(properties.class);

    // Address-related places
    if (osm_class === 'place') {
      if (['house', 'building', 'address'].includes(type)) {
        return 'address';
      }
      if (['city', 'town', 'village', 'hamlet'].includes(type)) {
        return 'place';
      }
    }

    // Administrative boundaries
    if (osm_class === 'boundary') {
      return 'region';
    }

    // Roads and transportation
    if (osm_class === 'highway') {
      return 'address';
    }

    // Points of interest
    if (['amenity', 'shop', 'tourism', 'leisure'].includes(osm_class)) {
      return 'poi';
    }

    return 'place';
  }

  /**
   * Build address context hierarchy
   */
  private buildAddressContext(address?: AddressComponents): Array<{
    id: string;
    text: string;
    short_code?: string;
  }> {
    if (!address) {
      return [];
    }

    const context = [];

    // Country
    if (address.country) {
      context.push({
        id: `country.${address.country_code}`,
        text: address.country,
        short_code: address.country_code?.toUpperCase(),
      });
    }

    // State/Voivodeship
    if (address.state) {
      context.push({
        id: `region.${address.state}`,
        text: address.state,
      });
    }

    // County/Powiat
    if (address.county) {
      context.push({
        id: `district.${address.county}`,
        text: address.county,
      });
    }

    // Municipality/Gmina
    if (address.municipality) {
      context.push({
        id: `locality.${address.municipality}`,
        text: address.municipality,
      });
    }

    // Postcode
    if (address.postcode) {
      context.push({
        id: `postcode.${address.postcode}`,
        text: address.postcode,
      });
    }

    return context;
  }

  /**
   * Handle and categorize errors
   */
  private handleError(error: unknown): GeocodingError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.code === 'ECONNABORTED') {
        return {
          type: GeocodingErrorType.TIMEOUT,
          message: 'Request timeout - Nominatim API did not respond in time',
          originalError: error,
        };
      }

      if (axiosError.response) {
        const status = axiosError.response.status;

        if (status === 429) {
          const retryAfter = axiosError.response.headers['retry-after'];
          return {
            type: GeocodingErrorType.RATE_LIMIT_EXCEEDED,
            message: 'Rate limit exceeded - too many requests',
            statusCode: status,
            retryAfter: retryAfter ? parseInt(retryAfter, 10) : 60,
            originalError: error,
          };
        }

        if (status >= 500) {
          return {
            type: GeocodingErrorType.SERVICE_UNAVAILABLE,
            message: 'Nominatim service is temporarily unavailable',
            statusCode: status,
            originalError: error,
          };
        }

        if (status === 400) {
          return {
            type: GeocodingErrorType.INVALID_QUERY,
            message: 'Invalid search query or parameters',
            statusCode: status,
            originalError: error,
          };
        }
      }

      return {
        type: GeocodingErrorType.NETWORK_ERROR,
        message: 'Network error - unable to reach Nominatim API',
        originalError: error,
      };
    }

    return {
      type: GeocodingErrorType.UNKNOWN_ERROR,
      message:
        error instanceof Error ? error.message : 'Unknown error occurred',
      originalError: error instanceof Error ? error : undefined,
    };
  }
}

/**
 * Default Nominatim service instance
 */
export const nominatimService = new NominatimService();
